/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useMemo } from "react";
import { FormControl, Tooltip } from "@mui/material";
import { InfoCircleOutlined } from "@ant-design/icons";
import { SelectedMenuItem } from "../selected_menu_item";
import { StyledMenuItem } from "../styled_menu_item";
import {
  profileMenuProps,
  StyledInactiveTag,
  StyledPickerContainer,
  StyledPickerHelperText,
  StyledPickerLabel,
  StyledProfileSelect,
  StyledRetryButton,
  StyledSelectedProfile,
  StyledSelectPlaceholder,
} from "./styles";

const PICKER_HINT =
  'A profile fills the tick-boxes below in one step. You can still change them afterwards — the profile is a starting point, not a limit.';

export const NO_PROFILE_VALUE = '';

/**
 * Dropdown that picks the permission profile a user's module selection starts from.
 *
 * Presentational: it owns no state and never applies a profile itself, because replacing the grid
 * may need confirming first (locked decision #12) and only the caller knows what is ticked.
 *
 * @param {Array}    options         profiles that may be picked for the roles now in the form
 * @param {String|Number} value      the selected profile id, or `''` for none
 * @param {Object}   selectedProfile the selected profile, offered as an option even when it is not
 *                                   in `options` — a deactivated one stays visible rather than
 *                                   being silently dropped from the user it is assigned to
 * @param {Object}   assignedProfile the profile stored against this user, kept on offer in its own
 *                                   right so clearing it is not a one-way door
 * @param {Function} onChange        called with the next profile id, or `''` when cleared
 * @param {Boolean}  hasRoles        whether any instance role is chosen, which is what profiles
 *                                   are filtered by
 * @param {Boolean}  notApplicable   the selected profile does not apply to the roles now chosen
 */
export const PermissionProfilePicker = ({
  options = [],
  value = NO_PROFILE_VALUE,
  selectedProfile = null,
  assignedProfile = null,
  onChange,
  isLoading = false,
  isError = false,
  onRetry,
  disabled = false,
  hasRoles = false,
  notApplicable = false,
  testIdPrefix = 'permission-profile-picker',
}) => {
  const menuOptions = useMemo(() => {
    const offered = Array.isArray(options) ? options : [];
    const seen = new Set(offered.map((option) => option?.id));
    const extras = [];

    [selectedProfile, assignedProfile].forEach((profile) => {
      if (!profile?.id || seen.has(profile.id)) return;

      seen.add(profile.id);
      extras.push(profile);
    });

    return extras.length === 0 ? offered : [...extras, ...offered];
  }, [options, selectedProfile, assignedProfile]);

  const helperText = useMemo(() => {
    if (isError) return { tone: 'warning', text: 'Could not load the profiles.', testId: 'error' };
    if (isLoading) return { tone: 'default', text: 'Loading profiles…', testId: 'loading' };
    if (notApplicable) {
      return {
        tone: 'warning',
        text:
          'This profile does not apply to the roles selected above. It is still recorded as where ' +
          'this user\'s modules came from — clear it if that is not what you want.',
        testId: 'not-applicable',
      };
    }
    if (!hasRoles) {
      return {
        tone: 'default',
        text: 'Add an instance role above to pick a profile.',
        testId: 'no-roles',
      };
    }
    if (menuOptions.length === 0) {
      return {
        tone: 'default',
        text: 'No active profile applies to the selected roles. Pick the modules by hand instead.',
        testId: 'no-matches',
      };
    }

    return null;
  }, [isError, isLoading, notApplicable, hasRoles, menuOptions.length]);

  const optionLabel = (profile) => (
    <StyledSelectedProfile>
      {profile?.name}
      {profile?.active === false && (
        <StyledInactiveTag
          label="Inactive"
          size="small"
          data-testid={`${testIdPrefix}-inactive-tag-${profile?.id}`}
        />
      )}
    </StyledSelectedProfile>
  );

  return (
    <StyledPickerContainer data-testid={testIdPrefix}>
      <StyledPickerLabel id={`${testIdPrefix}-label`}>
        Permission Profile
        <Tooltip title={PICKER_HINT} arrow placement="top">
          <InfoCircleOutlined style={{ fontSize: '15px', marginLeft: '5px' }} />
        </Tooltip>
      </StyledPickerLabel>

      <FormControl fullWidth size="small">
        <StyledProfileSelect
          labelId={`${testIdPrefix}-label`}
          data-testid={`${testIdPrefix}-select`}
          displayEmpty
          size="small"
          value={value}
          disabled={disabled || isLoading || isError}
          onChange={(event) => onChange?.(event.target.value)}
          MenuProps={profileMenuProps}
          renderValue={() => {
            if (value === NO_PROFILE_VALUE) {
              return <StyledSelectPlaceholder>None — pick the modules by hand</StyledSelectPlaceholder>;
            }
            if (selectedProfile) return optionLabel(selectedProfile);

            return <StyledSelectPlaceholder>Profile unavailable</StyledSelectPlaceholder>;
          }}
        >
          <StyledMenuItem value={NO_PROFILE_VALUE} data-testid={`${testIdPrefix}-option-none`}>
            {value === NO_PROFILE_VALUE ? (
              <SelectedMenuItem data="None — pick the modules by hand" />
            ) : (
              'None — pick the modules by hand'
            )}
          </StyledMenuItem>

          {menuOptions.map((profile) => (
            <StyledMenuItem
              key={profile?.id}
              value={profile?.id}
              data-testid={`${testIdPrefix}-option-${profile?.id}`}
            >
              {value === profile?.id ? <SelectedMenuItem data={profile?.name} /> : optionLabel(profile)}
            </StyledMenuItem>
          ))}
        </StyledProfileSelect>
      </FormControl>

      {helperText && (
        <StyledPickerHelperText
          tone={helperText.tone}
          data-testid={`${testIdPrefix}-${helperText.testId}`}
        >
          {helperText.text}
          {isError && typeof onRetry === 'function' && (
            <StyledRetryButton onClick={onRetry} data-testid={`${testIdPrefix}-retry`}>
              Retry
            </StyledRetryButton>
          )}
        </StyledPickerHelperText>
      )}
    </StyledPickerContainer>
  );
};

export default PermissionProfilePicker;
